import React from "react";
import { ArrowRightLeft, Copy, Eye, Headphones, Link2, MonitorPlay, MoreHorizontal, RefreshCcw } from "lucide-react";

import type {
  BindingMode,
  MediaBindingFamilyOption,
  MediaMember,
} from "./MediaUnion";

export interface MediaBindingControlsProps {
  variant?: "glass" | "panel";
  bindingMode: BindingMode;
  hasExplicitBinding: boolean;
  familyOptions: MediaBindingFamilyOption[];
  selectedFamilyId?: string;
  audioMembers: MediaMember[];
  videoMembers: MediaMember[];
  selectedAudioMemberId?: string;
  selectedVideoMemberId?: string;
  onSelectFamily?: (familyId: string) => void;
  onSelectAudioMember?: (memberId: string) => void;
  onSelectVideoMember?: (memberId: string) => void;
  onInspectMember?: (member: MediaMember, trackKind: "audio" | "video") => void;
  onUseCurrentProjection?: () => void;
  onClearExplicitBinding?: () => void;
}

function familySummary(option: MediaBindingFamilyOption): string {
  return `${option.audioMemberCount}A · ${option.videoMemberCount}V · ${option.clipCount} clip${option.clipCount === 1 ? "" : "s"}`;
}

function formatMetric(value: number | undefined, suffix: string, maxFractionDigits = 1): string | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: maxFractionDigits,
  })}${suffix}`;
}

function compactLabel(label: string | undefined): string {
  const safe = String(label ?? "").trim();
  if (!safe) return "Untitled member";
  return safe.length > 42 ? `${safe.slice(0, 39).trimEnd()}...` : safe;
}

function baseClipLabel(label: string | undefined): string {
  const safe = String(label ?? "").trim();
  if (!safe) return "No family selected";
  return safe.replace(/\.[a-z0-9]{2,5}$/i, "");
}

function originTone(originType: MediaBindingFamilyOption["originType"]): string {
  if (originType === "linkedMedia") return "border-cyan-200/28 bg-cyan-300/14 text-cyan-50";
  if (originType === "videoWithEmbeddedAudio") return "border-sky-200/28 bg-sky-300/14 text-sky-50";
  if (originType === "audioOnly") return "border-emerald-200/28 bg-emerald-300/14 text-emerald-50";
  return "border-white/18 bg-white/[0.08] text-white/86";
}

function memberStatusTone(isSelected: boolean): string {
  return isSelected
    ? "border-emerald-200/28 bg-emerald-300/14 text-emerald-50"
    : "border-white/12 bg-slate-950/18 text-white/76";
}

function memberMeta(member: MediaMember): string[] {
  const bits = [
    member.kind === "video" || member.kind === "previewProxy"
      ? formatMetric(member.frameRate, "fps")
      : null,
    member.kind === "audio" || member.kind === "embeddedAudio" || member.kind === "waveformProxy"
      ? formatMetric(member.sampleRate, "Hz", 0)
      : null,
    member.channelLayout ? member.channelLayout : null,
    formatMetric(member.durationSec, "s"),
  ].filter(Boolean) as string[];

  if (member.clipIds.length > 0) {
    bits.push(`${member.clipIds.length} clip${member.clipIds.length === 1 ? "" : "s"}`);
  }

  return bits;
}

function copyMemberLabel(label: string): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
    return Promise.resolve(false);
  }

  return navigator.clipboard.writeText(label).then(() => true).catch(() => false);
}

function glassTitleChipClassName(tone: "family" | "source" | "video" | "audio"): string {
  if (tone === "video") {
    return "inline-flex h-6 items-center rounded-full border border-sky-200/40 bg-[linear-gradient(180deg,rgba(191,219,254,0.92),rgba(125,211,252,0.72))] px-2.5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-950 shadow-[0_8px_18px_rgba(56,189,248,0.12)]";
  }
  if (tone === "audio") {
    return "inline-flex h-6 items-center rounded-full border border-emerald-200/40 bg-[linear-gradient(180deg,rgba(209,250,229,0.92),rgba(110,231,183,0.72))] px-2.5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-950 shadow-[0_8px_18px_rgba(16,185,129,0.12)]";
  }
  if (tone === "source") {
    return "inline-flex h-6 items-center rounded-full border border-cyan-200/36 bg-[linear-gradient(180deg,rgba(236,254,255,0.92),rgba(165,243,252,0.72))] px-2.5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-950 shadow-[0_8px_18px_rgba(8,145,178,0.10)]";
  }
  return "inline-flex h-6 items-center rounded-full border border-violet-200/34 bg-[linear-gradient(180deg,rgba(245,243,255,0.92),rgba(221,214,254,0.72))] px-2.5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-950 shadow-[0_8px_18px_rgba(139,92,246,0.10)]";
}

function MemberRow({
  title,
  members,
  selectedId,
  onSelect,
  onInspectMember,
  hasExplicitBinding,
  onUseCurrentProjection,
  onClearExplicitBinding,
  familySelected,
  variant,
}: {
  title: "Video Track" | "Audio Track";
  members: MediaMember[];
  selectedId?: string;
  onSelect?: (memberId: string) => void;
  onInspectMember?: (member: MediaMember, trackKind: "audio" | "video") => void;
  hasExplicitBinding: boolean;
  onUseCurrentProjection?: () => void;
  onClearExplicitBinding?: () => void;
  familySelected: boolean;
  variant: "glass" | "panel";
}): JSX.Element {
  const isPanel = variant === "panel";
  const selectedMember = members.find((member) => member.id === selectedId) ?? members[0] ?? null;
  const isVideo = title === "Video Track";
  const trackKind = isVideo ? "video" : "audio";
  const Icon = isVideo ? MonitorPlay : Headphones;
  const [inspectOpen, setInspectOpen] = React.useState(false);
  const [replaceOpen, setReplaceOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [copyState, setCopyState] = React.useState<"idle" | "done" | "failed">("idle");
  const accentClass = isVideo
    ? isPanel
      ? "border-sky-400/35 bg-sky-500/10 text-sky-200"
      : "border-sky-200/30 bg-sky-300/14 text-sky-50"
    : isPanel
      ? "border-emerald-400/35 bg-emerald-500/10 text-emerald-200"
      : "border-emerald-200/30 bg-emerald-300/14 text-emerald-50";
  const fieldClassName = isPanel
    ? "mt-2 h-10 rounded-xl border border-slate-700/70 bg-slate-900/80 px-3 text-[12px] text-slate-100 outline-none focus:border-sky-400/60"
    : "mt-2 h-9 rounded-full border border-white/12 bg-slate-950/24 px-3 text-[11px] text-white/92 outline-none focus:border-cyan-200/72";
  const actionButtonClassName = isPanel
    ? "inline-flex h-8 items-center justify-center gap-1 rounded-lg border border-slate-700/70 bg-slate-900/72 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-200 transition hover:bg-slate-800/84"
    : "inline-flex h-7 items-center justify-center gap-1 rounded-full border border-white/12 bg-slate-950/22 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/86 transition hover:bg-slate-950/30";
  const menuClassName = isPanel
    ? "mt-2 rounded-xl border border-slate-700/70 bg-slate-950/92 p-2"
    : "absolute right-0 top-full z-20 mt-2 min-w-[11rem] rounded-[16px] border border-white/12 bg-slate-950/72 p-2 shadow-[0_20px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl";
  const secondaryTextClassName = isPanel ? "text-[11px] text-slate-400" : "text-[11px] text-white/68";
  const actionWrapClassName = isPanel
    ? "flex flex-wrap items-center gap-1.5"
    : "relative flex flex-wrap items-center justify-end gap-1.5";
  const metadataToneClassName = isPanel
    ? "rounded-full border border-slate-700/70 bg-slate-900/75 px-2 py-1 text-[10px] font-medium text-slate-300"
    : "rounded-full border border-white/12 bg-slate-950/18 px-2 py-1 text-[10px] font-medium text-white/74";
  const glassTextShadowStyle = isPanel ? undefined : { textShadow: "0 1px 12px rgba(2,6,23,0.30)" };
  const headingScrimClassName = isPanel
    ? ""
    : "rounded-[14px] border border-white/10 bg-[linear-gradient(180deg,rgba(2,6,23,0.20),rgba(15,23,42,0.10))] px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]";
  const titleChipTone = isVideo ? "video" : "audio";

  React.useEffect(() => {
    setCopyState("idle");
  }, [selectedMember?.id]);

  const handleInspect = React.useCallback(() => {
    if (!selectedMember) return;
    onInspectMember?.(selectedMember, trackKind);
    setReplaceOpen(false);
    setInspectOpen((prev) => !prev || !onInspectMember);
    setMenuOpen(false);
  }, [onInspectMember, selectedMember, trackKind]);

  const handleReplaceToggle = React.useCallback(() => {
    setInspectOpen(false);
    setReplaceOpen((prev) => !prev);
    setMenuOpen(false);
  }, []);

  const handleSelect = React.useCallback((memberId: string) => {
    onSelect?.(memberId);
    setReplaceOpen(false);
  }, [onSelect]);

  const handleCopy = React.useCallback(async () => {
    if (!selectedMember) return;
    const didCopy = await copyMemberLabel(selectedMember.label);
    setCopyState(didCopy ? "done" : "failed");
    if (didCopy) {
      window.setTimeout(() => setCopyState("idle"), 1600);
    }
  }, [selectedMember]);

  const handlePrimaryBindAction = React.useCallback(() => {
    if (hasExplicitBinding) {
      onClearExplicitBinding?.();
    } else {
      onUseCurrentProjection?.();
    }
    setMenuOpen(false);
  }, [hasExplicitBinding, onClearExplicitBinding, onUseCurrentProjection]);

  return (
    <div className={isPanel ? "rounded-xl border border-slate-700/60 bg-slate-950/45 p-3" : "rounded-[18px] border border-white/12 bg-[linear-gradient(180deg,rgba(2,6,23,0.26),rgba(15,23,42,0.16))] px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-3">
          <div className={["flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] border", accentClass].join(" ")}>
            <Icon className="h-4 w-4" />
          </div>
          <div className={isPanel ? "min-w-0" : `min-w-0 ${headingScrimClassName}`}>
            <div className={isPanel ? "text-[11px] font-black uppercase tracking-[0.18em] text-slate-400" : glassTitleChipClassName(titleChipTone)} style={isPanel ? glassTextShadowStyle : undefined}>{title}</div>
            <div className={isPanel ? "mt-1 truncate text-[13px] font-semibold text-slate-100" : "mt-1 truncate text-[13px] font-semibold text-white/96"} style={glassTextShadowStyle}>
              {compactLabel(selectedMember?.label ?? `No ${isVideo ? "video" : "audio"} member`)}
            </div>
          </div>
        </div>
        <span className={["inline-flex h-7 items-center rounded-full border px-2.5 text-[10px] font-semibold uppercase tracking-[0.16em]", memberStatusTone(!!selectedMember)].join(" ")}>
          {selectedMember ? "Linked" : "Missing"}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {selectedMember ? (
          <div className="flex flex-wrap gap-2">
            {memberMeta(selectedMember).map((item) => (
              <span
                key={`${selectedMember.id}-${item}`}
                className={metadataToneClassName}
              >
                {item}
              </span>
            ))}
          </div>
        ) : <div className={secondaryTextClassName}>No linked member is available for this track.</div>}

        <div className={actionWrapClassName}>
          <button type="button" onClick={handleInspect} disabled={!selectedMember} className={actionButtonClassName} aria-label={`Inspect ${title}`}>
            <Eye className="h-3.5 w-3.5" />
            <span>Inspect</span>
          </button>
          <button type="button" onClick={handleReplaceToggle} disabled={!members.length || !onSelect} className={actionButtonClassName} aria-label={`Replace ${title}`}>
            <ArrowRightLeft className="h-3.5 w-3.5" />
            <span>Replace</span>
          </button>
          <button type="button" onClick={() => setMenuOpen((prev) => !prev)} className={actionButtonClassName} aria-label={`More actions for ${title}`}>
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>

          {menuOpen ? (
            <div className={menuClassName}>
              <div className="grid gap-1.5">
                <button type="button" onClick={handleInspect} disabled={!selectedMember} className={actionButtonClassName}>
                  <Eye className="h-3.5 w-3.5" />
                  <span>Metadata</span>
                </button>
                <button type="button" onClick={handleCopy} disabled={!selectedMember} className={actionButtonClassName}>
                  <Copy className="h-3.5 w-3.5" />
                  <span>{copyState === "done" ? "Copied" : copyState === "failed" ? "Failed" : "Copy"}</span>
                </button>
                <button
                  type="button"
                  onClick={handlePrimaryBindAction}
                  disabled={hasExplicitBinding ? !onClearExplicitBinding : !familySelected || !onUseCurrentProjection}
                  className={actionButtonClassName}
                >
                  {hasExplicitBinding ? <RefreshCcw className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                  <span>{hasExplicitBinding ? "Auto Bind" : "Projection"}</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {inspectOpen && selectedMember ? (
        <div className={isPanel ? "mt-2 rounded-xl border border-slate-700/70 bg-slate-950/78 px-3 py-2.5" : "mt-2 rounded-[16px] border border-cyan-200/18 bg-[linear-gradient(180deg,rgba(8,145,178,0.18),rgba(2,6,23,0.12))] px-3 py-2.5"}>
          <div className={isPanel ? "text-[10px] font-black uppercase tracking-[0.16em] text-sky-300/82" : "text-[10px] font-black uppercase tracking-[0.16em] text-cyan-100/82"}>
            Inspect Route
          </div>
          <div className={isPanel ? "mt-1 text-[11px] leading-5 text-slate-300" : "mt-1 text-[11px] leading-5 text-cyan-50/80"}>
            Routes this member into the active media authority without mutating the current binding.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {selectedMember.sourceAssetId ? <span className={secondaryTextClassName}>Asset {selectedMember.sourceAssetId}</span> : null}
            <span className={secondaryTextClassName}>{selectedMember.isPlayable ? "Playable" : "Reference only"}</span>
            <span className={secondaryTextClassName}>{selectedMember.isVisible ? "Visible" : "Hidden"}</span>
          </div>
        </div>
      ) : null}

      {replaceOpen || !selectedMember ? (
        <select
          value={selectedId ?? ""}
          onChange={(event) => handleSelect(event.target.value)}
          disabled={!members.length || !onSelect}
          className={fieldClassName}
          aria-label={title}
        >
          {!members.length ? <option value="">No {isVideo ? "video" : "audio"} member</option> : null}
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.label}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

export default function MediaBindingControls({
  variant = "glass",
  bindingMode,
  hasExplicitBinding,
  familyOptions,
  selectedFamilyId,
  audioMembers,
  videoMembers,
  selectedAudioMemberId,
  selectedVideoMemberId,
  onSelectFamily,
  onSelectAudioMember,
  onSelectVideoMember,
  onInspectMember,
  onUseCurrentProjection,
  onClearExplicitBinding,
}: MediaBindingControlsProps): JSX.Element {
  const isPanel = variant === "panel";
  const selectedFamily = familyOptions.find((option) => option.id === selectedFamilyId) ?? null;
  const cardClassName = isPanel
    ? "rounded-xl border border-slate-700/60 bg-slate-950/40 p-3"
    : "mt-3 rounded-[22px] border border-white/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(2,6,23,0.10)),radial-gradient(circle_at_top_left,rgba(34,211,238,0.08),rgba(34,211,238,0)_32%),radial-gradient(circle_at_80%_18%,rgba(45,212,191,0.06),rgba(45,212,191,0)_28%)] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]";
  const labelClassName = isPanel
    ? "text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400"
    : "text-[10px] font-semibold uppercase tracking-[0.16em] text-white/68";
  const selectClassName = isPanel
    ? "h-9 rounded-lg border border-slate-700/70 bg-slate-900/80 px-2 text-[12px] text-slate-100 outline-none focus:border-sky-400/60"
    : "h-10 rounded-[14px] border border-white/12 bg-slate-950/22 px-3 text-[12px] text-white/94 outline-none focus:border-cyan-200/72";
  const badgeClassName = isPanel
    ? "rounded-full border border-slate-700/70 bg-slate-900/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300"
    : "rounded-full border border-white/12 bg-slate-950/18 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/78";
  const buttonClassName = isPanel
    ? "rounded-lg border border-slate-700/70 bg-slate-900/80 px-3 py-2 text-[11px] font-semibold text-slate-100 transition hover:bg-slate-800/80"
    : "rounded-full border border-white/12 bg-slate-950/22 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/90 transition hover:bg-slate-950/30";
  const familySectionClassName = isPanel
    ? "mt-3 rounded-xl border border-slate-700/60 bg-slate-950/45 p-3"
    : "mt-3 rounded-[18px] border border-white/12 bg-[linear-gradient(180deg,rgba(2,6,23,0.22),rgba(15,23,42,0.14))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]";
  const familyInnerLayoutClassName = isPanel
    ? ""
    : "grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,24rem)] xl:items-end";
  const headerRowClassName = isPanel
    ? "mt-3 grid gap-3 md:grid-cols-2"
    : "mt-3 grid gap-3 xl:grid-cols-2";
  const footerRowClassName = isPanel
    ? "mt-3 flex flex-wrap items-center gap-2"
    : "mt-3 flex flex-wrap items-center justify-end gap-2";
  const familyHeadingScrimClassName = isPanel
    ? "min-w-0"
    : "min-w-0 rounded-[14px] border border-white/10 bg-[linear-gradient(180deg,rgba(2,6,23,0.20),rgba(15,23,42,0.10))] px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]";

  return (
    <div className={cardClassName}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={badgeClassName}>{bindingMode}</span>
        <span className={badgeClassName}>{hasExplicitBinding ? "Explicit member bind" : "Auto member bind"}</span>
      </div>

      <div className={familySectionClassName}>
        <div className={familyInnerLayoutClassName}>
          <div className="min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className={familyHeadingScrimClassName}>
                <div className={isPanel ? labelClassName : glassTitleChipClassName("family")}>Family Clip</div>
                <div className={isPanel ? "mt-1 text-[14px] font-semibold text-slate-100" : "mt-1 text-[14px] font-semibold text-white/92"}>
                  {baseClipLabel(selectedFamily?.label)}
                </div>
                <div className={isPanel ? "mt-1 text-[11px] text-slate-400" : "mt-1 text-[11px] text-white/52"}>
                  Parent family with scanable video and audio members.
                </div>
              </div>
              {selectedFamily ? (
                <span className={["inline-flex h-8 items-center rounded-full border px-3 text-[10px] font-semibold uppercase tracking-[0.16em]", originTone(selectedFamily.originType)].join(" ")}>
                  {familySummary(selectedFamily)}
                </span>
              ) : null}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={badgeClassName}>{videoMembers.length} video member{videoMembers.length === 1 ? "" : "s"}</span>
              <span className={badgeClassName}>{audioMembers.length} audio member{audioMembers.length === 1 ? "" : "s"}</span>
            </div>
          </div>

          <label className="flex min-w-0 flex-col gap-1.5 xl:self-end">
            <span className={isPanel ? labelClassName : glassTitleChipClassName("source")}>Family Source</span>
            <select
              value={selectedFamilyId ?? ""}
              onChange={(event) => onSelectFamily?.(event.target.value)}
              disabled={!familyOptions.length || !onSelectFamily}
              className={selectClassName}
            >
              {!selectedFamilyId ? <option value="">No family</option> : null}
              {familyOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {`${option.label} · ${familySummary(option)}`}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className={headerRowClassName}>
        <MemberRow
          title="Video Track"
          members={videoMembers}
          selectedId={selectedVideoMemberId}
          onSelect={onSelectVideoMember}
          onInspectMember={onInspectMember}
          hasExplicitBinding={hasExplicitBinding}
          onUseCurrentProjection={onUseCurrentProjection}
          onClearExplicitBinding={onClearExplicitBinding}
          familySelected={!!selectedFamilyId}
          variant={variant}
        />
        <MemberRow
          title="Audio Track"
          members={audioMembers}
          selectedId={selectedAudioMemberId}
          onSelect={onSelectAudioMember}
          onInspectMember={onInspectMember}
          hasExplicitBinding={hasExplicitBinding}
          onUseCurrentProjection={onUseCurrentProjection}
          onClearExplicitBinding={onClearExplicitBinding}
          familySelected={!!selectedFamilyId}
          variant={variant}
        />
      </div>

      <div className={footerRowClassName}>
        <button
          type="button"
          onClick={hasExplicitBinding ? onClearExplicitBinding : onUseCurrentProjection}
          disabled={hasExplicitBinding ? !onClearExplicitBinding : !selectedFamilyId || !onUseCurrentProjection}
          className={buttonClassName}
        >
          {hasExplicitBinding ? <><RefreshCcw className="mr-2 h-3.5 w-3.5" /> Auto bind</> : <><Link2 className="mr-2 h-3.5 w-3.5" /> Use current projection</>}
        </button>
      </div>
    </div>
  );
}